import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import useTripsQuery, { Trip, TripStatus } from "@/fetch/gworld/useTripsQuery";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import useCurrentTrip from "./useCurrentTrip";
import useSetTrip from "./useSetTrip";
import useFeatureFlags from "./useFeatureFlags";
import useCurrentPersonalData from "./useCurrentPersonalData";

const FINALISE_BOOKING_DATE_CONDITION = 1685577600; // 1 June 2023 00:00:00

const useManageNavigation = () => {
  const [isCurrentTripLocked, setIsCurrentTripLocked] = useState(false);
  const { isTripLocked } = useSetTrip();
  const {
    data: personalDetailsData,
    refetch: refetchPersonalDetails,
    isLoading: isPersonalDetailsLoading,
  } = usePersonalDetailsQuery();
  const { setCurrentPersonalData, currentPersonalData } =
    useCurrentPersonalData();

  const router = useRouter();

  const { currentTrip } = useCurrentTrip();

  const {
    flags: {
      paymentLock,
      eligibleForFullPaymentBanner,
      eligibleForPaymentPlanBanner,
    },
    isLoading: isFlagsLoading,
  } = useFeatureFlags();

  // const isProfileSetup = false; // uncomment to test the setup page

  const isSalesRegisterProcessed = Boolean(
    currentTrip?.sales_register_processed
  );

  const isTripCancelling = currentTrip?.status === TripStatus.Cancelling;
  const isPaymentLockEnabled = Boolean(paymentLock);

  /* Should redirect to the lock page when profile is set and trip is locked.
        Maybe user locked out of gworld because she did not complete the personal details yet,
        so she should see setup stepper page instead of lock page.
        */
  const shouldRedirectToLockPage = isCurrentTripLocked;

  useEffect(() => {
    const isLocked = isTripLocked(currentTrip);
    setIsCurrentTripLocked(isLocked);
  }, [currentTrip]);

  const shouldRedirectToFinalisePage = useMemo(
    () =>
      currentTrip &&
      currentTrip?.status !== TripStatus.Complete &&
      !shouldRedirectToLockPage &&
      !isSalesRegisterProcessed &&
      (currentTrip?._ca || 0) > FINALISE_BOOKING_DATE_CONDITION,
    [
      currentTrip?._ca,
      currentTrip?.status,
      isSalesRegisterProcessed,
      shouldRedirectToLockPage,
    ]
  );

  const isProfileSetup =
    typeof currentPersonalData?.traveller?.updated_profile === "number"
      ? Boolean(currentPersonalData?.traveller?.updated_profile)
      : null;

  useEffect(() => {
    /* To prevent routing through pages when profile is not setup */
    const handleRouteChange = (url: string) => {
      if (!currentTrip || !currentTrip.id) return;
      if (isProfileSetup === false && !url.includes("setup")) {
        if (
          (shouldRedirectToLockPage && url.includes("/lock")) ||
          (shouldRedirectToFinalisePage && url.includes("/finalise")) ||
          (!shouldRedirectToLockPage && url === "/") ||
          (!shouldRedirectToFinalisePage && url === "/")
        ) {
          return;
        }
        throw `Access to "${url}" denied, profile is not setup yet.`;
      }
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [
    router.events,
    isProfileSetup,
    shouldRedirectToLockPage,
    shouldRedirectToFinalisePage,
    currentTrip,
  ]);

  useEffect(() => {
    /* To prevent routing through pages when trip status is cancelling*/
    const handleRouteChange = (url: string) => {
      if (!currentTrip || !currentTrip.id) return;
      if (isTripCancelling && !url.includes("/cancellation")) {
        if (url.includes("/experience/dashboard?tripChanged")) {
          /* Allow trip change by trip selector when current trip is locked 
                 and lock page is rendering */
          return;
        }
        throw "Access denied, please complete the cancellation flow.";
      }
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events, isTripCancelling, currentTrip]);

  useEffect(() => {
    /* To prevent routing through pages when trip is locked (cancelled, on-hold, etc.)*/
    const handleRouteChange = (url: string) => {
      if (!currentTrip || !currentTrip.id) return;
      if (shouldRedirectToLockPage && !url.includes("lock")) {
        if (url.includes("/experience/dashboard?tripChanged")) {
          /* Allow trip change by trip selector when current trip is locked 
                 and lock page is rendering */
          return;
        }

        throw `Access to "${url}" denied, you are locked out from gWorld.`;
      }
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events, shouldRedirectToLockPage, currentTrip]);

  useEffect(() => {
    /* redirect to the right page based on different scenarios */
    if (!currentTrip || !currentTrip.id) return;
    if (isTripCancelling && !router.pathname.includes("/cancellation")) {
      router.replace({ pathname: "/cancellation" });
      return;
    } else if (
      shouldRedirectToFinalisePage &&
      !router.pathname.includes("/finalise")
    ) {
      router.replace({ pathname: "/finalise" });
      return;
    } else if (shouldRedirectToLockPage && !router.pathname.includes("/lock")) {
      router.replace({ pathname: "/lock", query: router.query });
      return;
    } else if (
      !shouldRedirectToLockPage &&
      !shouldRedirectToFinalisePage &&
      !isTripCancelling &&
      isProfileSetup === false &&
      !router.pathname.includes("/setup")
    ) {
      router.replace({ pathname: "/setup" });
      return;
    } else if (!shouldRedirectToLockPage && router.pathname.includes("/lock")) {
      router.replace({ pathname: "/" });
      return;
    } else if (
      isProfileSetup &&
      isPaymentLockEnabled &&
      !shouldRedirectToFinalisePage &&
      !isTripCancelling &&
      !shouldRedirectToLockPage &&
      !router.pathname.includes("/experience/payments")
    ) {
      router.replace({
        pathname: "/experience/payments/invoice/trip",
        query: router.query,
      });
    }
  }, [
    router.pathname,
    isPersonalDetailsLoading,
    isTripCancelling,
    router,
    isProfileSetup,
    shouldRedirectToLockPage,
    shouldRedirectToFinalisePage,
    isPaymentLockEnabled,
    currentTrip,
  ]);

  useEffect(() => {
    /* To prevent routing through pages when isPaymentLockEnabled is enabled */
    const handleRouteChange = (url: string) => {
      if (!currentTrip || !currentTrip.id) return;
      if (isPaymentLockEnabled && !url.includes("/experience/payments")) {
        if (url.includes("/experience/dashboard?tripChanged")) {
          /* Allow trip change by trip selector when current trip is locked 
             and lock page is rendering */
          return;
        }

        if (
          (!isProfileSetup && url.includes("/setup")) ||
          (isTripCancelling && url.includes("/cancellation")) ||
          (shouldRedirectToLockPage && url.includes("/lock")) ||
          (shouldRedirectToFinalisePage && url.includes("/finalise")) ||
          (!shouldRedirectToLockPage && url === "/") ||
          (!shouldRedirectToFinalisePage && url === "/")
        ) {
          /* Allow going to setup, cancellation, lock or / when it's needed */
          return;
        }
        throw `Access to "${url}" denied, payment lock is enabled.`;
      }
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [
    router.events,
    isProfileSetup,
    isPaymentLockEnabled,
    isTripCancelling,
    shouldRedirectToLockPage,
    shouldRedirectToFinalisePage,
    currentTrip,
  ]);

  const isTripLockedForUnderAgeWaitingApproval = (
    trip: Trip | undefined | null
  ) => {
    // since parent information now filled through gWorld, we want traveller to first
    // fill the parent info and then lock them, so if personal details are not filled yet
    // we let them to continue the setup process and then lock them

    const isLockedForUnderAgeWaitingApproval =
      trip?.status === TripStatus.UnderAgeWaitingApproval && isProfileSetup;
    return isLockedForUnderAgeWaitingApproval;
  };

  // Used for disabling links in pages that takes the user outside of the current page
  const isAllowedToNavigate =
    isProfileSetup &&
    !isTripCancelling &&
    !isCurrentTripLocked &&
    !isPaymentLockEnabled &&
    !shouldRedirectToFinalisePage &&
    currentTrip;

  useEffect(() => {
    if (personalDetailsData) {
      setCurrentPersonalData(personalDetailsData);
    }
  }, [currentPersonalData, personalDetailsData]);

  // console.log("----------------------");
  // console.log(isPersonalDetailsLoading);
  // console.log(currentPersonalData);
  // console.log("----------------------");

  return {
    isTripLockedForUnderAgeWaitingApproval,
    isAllowedToNavigate,
    isPaymentLockEnabled,
    isProfileSetup,
    isCurrentTripLocked,
    isLoading: isPersonalDetailsLoading && !currentPersonalData,
  };
};

export default useManageNavigation;
